import React, { useMemo, useState } from 'react';
import { useGlobalStorageContext } from '../contexts/GlobalStorageContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { buildPrayerTimesForDate, formatTime } from '../services/prayerService';
import './MonthlyPage.css';

export const MonthlyPage: React.FC = () => {
  const { colors } = useTheme();
  const { t } = useLanguage();
  const { manualLocation, calcSettings } = useGlobalStorageContext();
  const [currentDate, setCurrentDate] = useState(new Date());

  const monthlyTimes = useMemo(() => {
    if (!manualLocation || !calcSettings) return [];
    
    const times = [];
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const pt = buildPrayerTimesForDate('', date, manualLocation, calcSettings);
      times.push({
        date: day,
        isToday: date.toDateString() === new Date().toDateString(),
        fajr: formatTime(pt.fajr),
        sunrise: formatTime(pt.sunrise),
        dhuhr: formatTime(pt.dhuhr),
        asr: formatTime(pt.asr),
        maghrib: formatTime(pt.maghrib),
        isha: formatTime(pt.isha),
      });
    }
    return times;
  }, [manualLocation, calcSettings, currentDate]);

  const changeMonth = (offset: number) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + offset);
    setCurrentDate(newDate);
  };

  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="monthly-page" style={{ backgroundColor: colors.background, color: colors.text }}>
      <div className="month-nav" style={{ backgroundColor: colors.cardBackground, borderColor: colors.border }}>
        <button onClick={() => changeMonth(-1)} className="nav-btn" style={{ color: colors.tint }}>‹</button>
        <h2 style={{ margin: 0 }}>{monthName}</h2>
        <button onClick={() => changeMonth(1)} className="nav-btn" style={{ color: colors.tint }}>›</button>
      </div>

      <div className="monthly-table-container">
        <table className="monthly-table" style={{ borderColor: colors.border }}>
          <thead>
            <tr style={{ backgroundColor: colors.cardBackground }}>
              <th>Date</th>
              <th>{t.prayer.fajr}</th>
              <th>{t.prayer.sunrise}</th>
              <th>{t.prayer.dhuhr}</th>
              <th>{t.prayer.asr}</th>
              <th>{t.prayer.maghrib}</th>
              <th>{t.prayer.isha}</th>
            </tr>
          </thead>
          <tbody>
            {monthlyTimes.map((day) => (
              <tr key={day.date} style={{ borderBottomColor: colors.border, backgroundColor: day.isToday ? colors.tint + '20' : 'transparent' }}>
                <td style={{ fontWeight: day.isToday ? 'bold' : 600, color: day.isToday ? colors.tint : colors.text }}>{day.date}</td>
                <td style={{ fontWeight: day.isToday ? 'bold' : 'normal', color: day.isToday ? colors.tint : colors.text }}>{day.fajr}</td>
                <td style={{ fontWeight: day.isToday ? 'bold' : 'normal', color: day.isToday ? colors.tint : colors.text }}>{day.sunrise}</td>
                <td style={{ fontWeight: day.isToday ? 'bold' : 'normal', color: day.isToday ? colors.tint : colors.text }}>{day.dhuhr}</td>
                <td style={{ fontWeight: day.isToday ? 'bold' : 'normal', color: day.isToday ? colors.tint : colors.text }}>{day.asr}</td>
                <td style={{ fontWeight: day.isToday ? 'bold' : 'normal', color: day.isToday ? colors.tint : colors.text }}>{day.maghrib}</td>
                <td style={{ fontWeight: day.isToday ? 'bold' : 'normal', color: day.isToday ? colors.tint : colors.text }}>{day.isha}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
